import { useMemo } from 'react'
import { TokenType } from '@components/Tokens/types'
import { isAddress } from '@components/Web3/Wallet/useGetWalletParam'
import { isDefined } from '@towns-protocol/sdk'
import { useCollectionsForAddressesAcrossNetworks, useNftMetadata } from 'api/lib/tokenContracts'
import { useLinkedAndDelegatedWallets } from 'use-towns-client'

export const useNfts = (args: { walletAddress?: string; enabled?: boolean }) => {
    const { walletAddress, enabled } = args
    const _enabled = enabled !== undefined ? enabled : true
    const { data: wallets = [] } = useLinkedAndDelegatedWallets({
        walletAddress,
        enabled: !!walletAddress,
    })
    const { data: nfts, isFetching } = useCollectionsForAddressesAcrossNetworks({
        wallets: walletAddress
            ? wallets.includes(walletAddress)
                ? wallets
                : wallets.concat(walletAddress)
            : wallets,
        enabled: isAddress(walletAddress) && _enabled,
    })

    return {
        nfts: nfts.filter(isDefined).filter((nft) => nft.data.type === TokenType.ERC721),
        isFetching,
    }
}

export const useResolveNft = ({
    walletAddress,
    info,
}: {
    walletAddress: string
    info: { chainId: number; tokenId: string; contractAddress: string } | undefined
}) => {
    const { data: metadata } = useNftMetadata(info)
    const linkedWallets = useLinkedAndDelegatedWallets({ walletAddress, enabled: !!info })

    const nft = useMemo(() => {
        if (!metadata || !linkedWallets.data) {
            return undefined
        }

        const lowerCasedWallets = linkedWallets.data.map((w) => w.toLowerCase())
        const lowerCasedOwners = metadata.data.owners.map((w) => w.toLowerCase())

        // ⚠️ Important: the owners array could be empty
        // the data we get from alchemy sometimes either has no owners, or the request for owners might fail - i.e. requesting owners on polygon, alchemy errors out
        // so if users are complaining that something is not showing up, we could maybe remove this check
        const isOwner = lowerCasedOwners.some((owner) => lowerCasedWallets.includes(owner))
        if (!isOwner) {
            return undefined
        }
        return {
            title: metadata.data.name,
            description: metadata.data.description,
            image: metadata.data.image,
        }
    }, [metadata, linkedWallets])

    return nft
}
